import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../api";
import { ReactComponent as DeleteIcon } from "../../assets/images/icons/ic-delete.svg";
import { ReactComponent as DownloadIcon } from "../../assets/images/icons/ic-download.svg";
import { ReactComponent as EditIcon } from "../../assets/images/icons/ic-edit.svg";
import ThumbnailExcel from "../../assets/images/illustrations/il-thumbnail-excel.svg";
import ThumbnailGeneric from "../../assets/images/illustrations/il-thumbnail-generic.svg";
import ThumbnailImage from "../../assets/images/illustrations/il-thumbnail-image.svg";
import ThumbnailPdf from "../../assets/images/illustrations/il-thumbnail-pdf.svg";
import ThumbnailPpt from "../../assets/images/illustrations/il-thumbnail-ppt.svg";
import ThumbnailText from "../../assets/images/illustrations/il-thumbnail-text.svg";
import ThumbnailVideo from "../../assets/images/illustrations/il-thumbnail-video.svg";
import { DialogStatus } from "../../enums";
import { formatDateV2, formatFileSize, formatTimeV2 } from "../../utils";
import Button from "../Button";
import DropdownSelection from "../DropdownSelection";
import MeatBallsMenu from "../MeatBallsMenu";
import AlertDialog from "../dialogs/AlertDialog";
import Dialog from "../dialogs/Dialog";
import styles from "./MaterialButton.module.css";

const MaterialButton = ({ material, lesson = false, thumbnail = false, modules = [], loadingModules = false, style = {}, onDelete = () => { }, onEdit = () => { } }) => {

  const { t } = useTranslation();
  const [type, setType] = useState(null)
  const [openAlert, setOpenAlert] = useState(false)
  const [alertStatus, setAlertStatus] = useState(DialogStatus.Default)
  const [closeTimeout, setCloseTimeout] = useState(null)
  const [closeEditTimeout, setCloseEditTimeout] = useState(null)

  const [openEditDialog, setOpenEditDialog] = useState(false)
  const [lessons, setLessons] = useState([])
  const [selectedModule, setSelectedModule] = useState(null)
  const [selectedLesson, setSelectedLesson] = useState(null)
  const [editStatus, setEditStatus] = useState(DialogStatus.Default)

  const imagesExtensions = ["jpg", "jpeg", "png", "svg", "webp", "gif", "avif", "apng", "heic"]
  const videoExtensions = ["mp4", "m4p", "mov", "flv", "mkv", "avi", "wmv", "mpg", "mpeg", "m2v"]
  const pptExtensions = ["ppt", "pptx"]
  const excelExtensions = ["xlsx", "xls"]
  const textExtensions = ["docx", "doc"]

  useEffect(() => {
    if (material) {
      const supportedImageExtensions = ["jpg", "jpeg", "png", "svg", "webp", "gif", "avif", "apng"]
      const supportedVideoExtensions = ["mp4", "m4p", "mov", "flv", "mkv", "avi", "wmv", "mpg", "mpeg", "m2v"]
      const fileExtension = material.url.split(".").slice(-1)[0].toLowerCase()
      if (supportedImageExtensions.includes(fileExtension)) {
        setType("image")
      } else if (supportedVideoExtensions.includes(fileExtension)) {
        setType("video")
      } else if (fileExtension === "pdf") {
        setType("pdf")
      } else {
        setType(null)
      }
    }
  }, [material])

  useEffect(() => {
    return () => {
      if (closeTimeout) {
        clearTimeout(closeTimeout)
      }
      if (closeEditTimeout) {
        clearTimeout(closeEditTimeout)
      }
    }
  }, [])

  useEffect(() => {
    if (!openEditDialog) {
      setSelectedModule(null)
      setSelectedLesson(null)
      setLessons([])
      setEditStatus(DialogStatus.Default)
      setAlertStatus(DialogStatus.Default)
    } else {
      const { module, lesson } = material;
      const moduleIdx = modules.findIndex((m) => m.id === module.id)
      if (moduleIdx >= 0) {
        setSelectedModule(moduleIdx)

        const { lessons } = modules[moduleIdx]
        const l = lessons.map((l) => {
          return {
            ...l,
            label: l.name
          }
        })
        l.push({ label: "Nessuna lezione", id: null })
        setLessons(l)

        if (lesson) {
          const lessonIdx = lessons.findIndex((l) => l.id === lesson.id)
          if (lessonIdx >= 0) {
            setSelectedLesson(lessonIdx)
          }
        }
      }
    }
  }, [openEditDialog])

  useEffect(() => {
    if (!openAlert) setAlertStatus(DialogStatus.Default)
  }, [openAlert])

  useEffect(() => {
    if (selectedModule >= 0 && modules[selectedModule] && selectedLesson === null) {
      const { lessons } = modules[selectedModule]
      const l = lessons.map((l) => {
        return {
          ...l,
          label: l.name
        }
      })
      l.push({ label: "Nessuna lezione", id: null })
      setLessons(l)
    }
  }, [selectedModule, selectedLesson])

  const openMaterial = useCallback(() => {
    const newWindow = window.open("", "_blank")
    let content = ""
    switch (type) {
      case "image":
        content = `<img src="${material.url}" alt=""/>`
        break
      case "video":
        content = `
                <video width="320" height="240" controls>
                    <source src="${material.url}">
                </video>`
        break
      case "pdf":
        content = `<embed src="${material.url}" width="500" height="375" type="application/pdf">`
        break
      default: break
    }

    newWindow.document.write(`
            <html>
                <head>
                </head>
                <body>
                ${content}
                </body>
            </html>
        `);



  }, [material, type])

  const editMaterial = useCallback(async () => {
    setEditStatus(DialogStatus.Loading)
    try {
      const body = {}
      body.module_id = modules[selectedModule].id
      if (selectedLesson !== null) {
        body.lesson_id = lessons[selectedLesson].id
      }
      const newMaterial = await api.put(`/teacher/materials/${material.id}`, body, { timeout: 0 })

      newMaterial.module = modules[selectedModule]
      if (selectedLesson !== null) {
        newMaterial.lesson = lessons[selectedLesson]
      }
      setEditStatus(DialogStatus.Success)
      onEdit(newMaterial)
      setCloseEditTimeout(setTimeout(() => setOpenEditDialog(false), 3000))

    } catch (e) {
      console.error(e)
      setEditStatus(DialogStatus.Error)
      setCloseEditTimeout(setTimeout(() => setOpenEditDialog(false), 3000))
    }
  }, [material, selectedModule, selectedLesson, modules, lessons, onEdit])

  const deleteMaterial = useCallback(async () => {
    try {
      setAlertStatus(DialogStatus.Loading)
      await api.delete(`/teacher/materials/${material.id}`, { timeout: 0 })
      onDelete(material.id)
      setAlertStatus(DialogStatus.Success)

      setCloseTimeout(setTimeout(() => setOpenAlert(false), 3000))
    } catch (e) {
      console.error(e)
      setAlertStatus(DialogStatus.Error)

      setCloseTimeout(setTimeout(() => {
        setOpenAlert(false)
      }, 3000))
    }
  }, [material, onDelete])

  const downloadMaterial = (url) => {
    const a = document.createElement("a")
    a.href = url
    a.download = material.name
    a.click()
  }

  const Thumbnail = useMemo(() => {
    if (material) {
      const split = material.name.split(".")
      const fileExtension = split.length > 1 ? split[split.length - 1].toLowerCase() : ""
      if (videoExtensions.includes(fileExtension)) {
        return ThumbnailVideo
      }
      if (imagesExtensions.includes(fileExtension)) {
        return ThumbnailImage
      }
      if (pptExtensions.includes(fileExtension)) {
        return ThumbnailPpt
      }
      if (pptExtensions.includes(fileExtension)) {
        return ThumbnailPpt
      }
      if (excelExtensions.includes(fileExtension)) {
        return ThumbnailExcel
      }
      if (textExtensions.includes(fileExtension)) {
        return ThumbnailText
      }
      if (fileExtension === "pdf") {
        return ThumbnailPdf
      }
    }
    return ThumbnailGeneric
  }, [material])

  return (
    <>
      <div className={styles.material} style={style}>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '0.5rem', width: '100%' }}>
          {thumbnail &&
            <img src={Thumbnail} className={styles.thumbnail} alt={material.name} />
          }
          <div className={styles.info}>
            <div className={styles.name}>{material.name}</div>
            <div className={styles.size}>{formatFileSize(material.size)} | {formatDateV2(material.created_at, { day: '2-digit', month: '2-digit', year: 'numeric' })} - {formatTimeV2(material.created_at)}</div>
            {lesson && material.lesson &&
              <a target="_blank" rel="noreferrer" href={`/lessons/${material.lesson.room_name}`} className={styles.lesson}>{material.lesson?.name}</a>
            }
          </div>
          <div className={styles.menu}>
            <MeatBallsMenu
              disabled={loadingModules}
              actions={
                [
                  {
                    label: t("download"), icon: DownloadIcon, style: { color: "var(--sf-green)" }, onClick: () => downloadMaterial(material.url)
                  },
                  {
                    label: t("edit"), icon: EditIcon, style: { color: "var(--primary)" }, onClick: () => setOpenEditDialog(true)
                  },
                  {
                    label: t("delete"), icon: DeleteIcon, style: { color: "var(--secondary)" }, onClick: () => setOpenAlert(true)
                  }
                ]} />
          </div>
        </div>

      </div>
      <AlertDialog
        open={openAlert}
        title={t("materials.deleteTitle")}
        text={t("materials.deleteText")}
        status={alertStatus}
        onClose={() => {
          setOpenAlert(false)
          if (closeTimeout) {
            clearTimeout(closeTimeout)
          }
        }}
        actions={[
          {
            label: t("materials.deleteConfirm").toUpperCase(),
            onClick: deleteMaterial,
          }
        ]}
      />

      <Dialog
        overflow="visible"
        open={openEditDialog}
        title={t("materials.editTitle")}
        style={{ maxWidth: "512px" }}
        status={editStatus}
        onClose={() => {
          setOpenEditDialog(false)
          if (closeEditTimeout) {
            clearTimeout(closeEditTimeout)
          }
        }}
        action={
          <Button onClick={editMaterial} disabled={editStatus !== DialogStatus.Default}>
            {t("materials.save").toUpperCase()}
          </Button>
        }
      >
        <div className={styles.dialog}>

          <DropdownSelection
            disabled={modules.length === 0}
            placeholder={"Associa un modulo"}
            defaultOptionIndex={selectedModule}
            options={modules}
            appereance="transparent"
            onSelect={(index) => {
              if (index !== selectedModule) {
                setSelectedModule(index)
                setSelectedLesson(null)
              }
            }} />

          <DropdownSelection
            disabled={lessons.length === 0}
            placeholder={"Associa una lezione"}
            defaultOptionIndex={selectedLesson}
            options={lessons}
            appereance="transparent"
            onSelect={(index) => setSelectedLesson(index)} />

        </div>
      </Dialog>
    </>
  );
};

export default MaterialButton;
